import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  TextField
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error, Email } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';
import api from '../../../services/api';

const ModelUpload = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [validationResult, setValidationResult] = useState(null);
  const [badRecords, setBadRecords] = useState([]);
  const [showBadRecords, setShowBadRecords] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [emailConfig, setEmailConfig] = useState({ manager: '', cc: '' });

  useEffect(() => {
    api.get('/email-setup').then(res => {
      if (res.data.success) {
        const mgr = res.data.data.find(e => e.general_code === 'MNDI');
        const fin = res.data.data.find(e => e.general_code === 'FCSI');
        setEmailConfig({ manager: mgr?.general_value || '', cc: fin?.general_value || '' });
      }
    }).catch(() => {});
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['.xlsx', '.xls'];
      const isValid = validTypes.some(type => file.name.toLowerCase().endsWith(type));
      
      if (!isValid) {
        setMessage({ text: 'Please select a valid Excel file (.xls, .xlsx)', type: 'error' });
        return;
      }
      
      setSelectedFile(file);
      setMessage({ text: '', type: '' });
      setValidationResult(null);
      setBadRecords([]);
      setUploadResult(null);
    }
  };

  const handleValidateFile = async () => {
    if (!selectedFile) {
      setMessage({ text: 'Please select a file first', type: 'error' });
      return;
    }

    setValidateLoading(true);
    setMessage({ text: '', type: '' });

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('user', JSON.stringify({
      username: user?.username || 'system',
      id: user?.userId || user?.id || 1
    }));

    try {
      await api.post(`${API_ENDPOINTS.MODEL_UPLOAD}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const validateResponse = await api.post(`${API_ENDPOINTS.MODEL_UPLOAD}/validate`, {
        user: {
          username: user?.username || 'system',
          id: user?.userId || user?.id || 1
        }
      });

      const result = validateResponse.data;
      setValidationResult(result);

      if (result.validationCode === 0) {
        setMessage({ text: 'File validated successfully. Ready to upload.', type: 'success' });
      } else {
        const errorResponse = await api.get(`${API_ENDPOINTS.MODEL_UPLOAD}/errors`);
        setBadRecords(errorResponse.data);
        setMessage({ 
          text: `Validation failed. Found ${result.errorCount} invalid records out of ${result.totalRecords} total records.`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Validation error:', error);
      setMessage({ text: 'Failed to validate file. Please try again.', type: 'error' });
    } finally {
      setValidateLoading(false);
    }
  };

  const handleUploadData = async () => {
    if (!validationResult || validationResult.validationCode !== 0) {
      setMessage({ text: 'Please validate the file first', type: 'error' });
      return;
    }

    setUploadLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await api.post(`${API_ENDPOINTS.MODEL_UPLOAD}/process`, {
        user: {
          username: user?.username || 'system',
          id: user?.userId || user?.id || 1
        },
        emailConfig
      });

      const result = response.data;
      setUploadResult(result);

      if (result.success) {
        if (result.failedRecords && result.failedRecords.length > 0) {
          setBadRecords(result.failedRecords);
          setMessage({ 
            text: `Data uploaded successfully! ${result.processedRecords} records processed. ${result.errorCount} records failed validation. Email notification sent.`, 
            type: 'warning' 
          });
        } else {
          setMessage({ text: 'Data uploaded successfully! Email notification sent.', type: 'success' });
        }
        setSelectedFile(null);
        setValidationResult(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setMessage({ text: result.error || 'Upload failed', type: 'error' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ text: 'Failed to upload data. Please try again.', type: 'error' });
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Create Excel workbook instead of CSV
    const templateData = [
      ['Project ID', 'From Model', 'To Model', 'Quantity', 'Required Date (MM/DD/YYYY)'],
      ['A01AA001', 'MODEL001', 'MODEL002', '100', '01/15/2024'],
      ['B02BB002', 'MODEL003', '', '50', '01/20/2024']
    ];

    // Create Excel content
    const excelContent = templateData.map(row => row.join('\t')).join('\n');
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'model_upload_template.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }} role="main">
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>Model Upload</Typography>

      {(uploadLoading || validateLoading) && (
        <LinearProgress sx={{ mb: 2 }} aria-label="Loading" />
      )}

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} role="alert">
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email aria-hidden="true" /> Email Configuration
          </Typography>
          <TextField
            fullWidth
            label="Manager Email"
            value={emailConfig.manager}
            onChange={(e) => setEmailConfig(prev => ({ ...prev, manager: e.target.value }))}
            placeholder="manager@example.com"
            helperText="Separate multiple emails with semicolon (;)"
            inputProps={{ 'aria-label': 'Manager email addresses', maxLength: 500 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="CC Email"
            value={emailConfig.cc}
            onChange={(e) => setEmailConfig(prev => ({ ...prev, cc: e.target.value }))}
            placeholder="finance@example.com"
            helperText="Separate multiple emails with semicolon (;)"
            inputProps={{ 'aria-label': 'CC email addresses', maxLength: 500 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>File Upload</Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xls,.xlsx"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Select File
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadTemplate}
            >
              Download Template
            </Button>
          </Box>

          {selectedFile && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleValidateFile}
              disabled={!selectedFile || validateLoading}
              startIcon={validateLoading ? <CircularProgress size={16} /> : <CheckCircle />}
            >
              {validateLoading ? 'Validating...' : 'Validate File'}
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={handleUploadData}
              disabled={!validationResult || validationResult.validationCode !== 0 || uploadLoading}
              startIcon={uploadLoading ? <CircularProgress size={16} /> : <CloudUpload />}
            >
              {uploadLoading ? 'Uploading...' : 'Upload Data'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {validationResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Validation Results</Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Typography variant="body2">
                Total Records: <strong>{validationResult.totalRecords}</strong>
              </Typography>
              {validationResult.errorCount > 0 && (
                <Typography variant="body2" color="error">
                  Invalid Records: <strong>{validationResult.errorCount}</strong>
                </Typography>
              )}
            </Box>

            {badRecords.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Error />}
                onClick={() => setShowBadRecords(true)}
              >
                View Invalid Records ({badRecords.length})
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {uploadResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Upload Results</Typography>
            <Typography variant="body2">
              Status: <strong>{uploadResult.success ? 'Success' : 'Failed'}</strong>
            </Typography>
            {uploadResult.processedRecords && (
              <Typography variant="body2">
                Records Processed: <strong>{uploadResult.processedRecords}</strong>
              </Typography>
            )}
            {uploadResult.errorCount > 0 && (
              <>
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Failed Records: <strong>{uploadResult.errorCount}</strong>
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Error />}
                  onClick={() => setShowBadRecords(true)}
                  sx={{ mt: 2 }}
                >
                  View Failed Records ({uploadResult.errorCount})
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog 
        open={showBadRecords} 
        onClose={() => setShowBadRecords(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Invalid Records</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Project ID</TableCell>
                  <TableCell>From Model</TableCell>
                  <TableCell>To Model</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {badRecords.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.projectid}</TableCell>
                    <TableCell>{record.from_model}</TableCell>
                    <TableCell>{record.to_model}</TableCell>
                    <TableCell>{record.quantity}</TableCell>
                    <TableCell>{record.created_date}</TableCell>
                    <TableCell>{record.created_by}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{record.error_code}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBadRecords(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelUpload;